import React, { useState, useEffect, useContext } from "react";
import _ from "lodash";
import imageCompression from "browser-image-compression";
import { v4 } from "uuid";
import styles from "./CompanyInfo.module.scss";
import { useQuery, useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { FetchContext } from "../../context/fetchContext";
import { SnackbarContext } from "../../context/snackbarContext";
import { useAuth } from "../../context/authContext";
import { useDepartmentFilter } from "../../context/departmentFilterContext";
import { isAuthed } from "../../utils/authorization";
import { Container, Grid, Typography, Avatar, Divider, Hidden, Button, MenuItem } from "@material-ui/core";
import Menu from "../../components/Menu/Menu";
import Icon from "@mdi/react";
import { mdiCalendar } from "@mdi/js";
import Loading from "../../components/Loading/Loading";
import AvatarEditDialog from "../../components/AvatarEditDialog/AvatarEditDialog";
import EditDialog from "./EditDialog";

import { fiscalYearStart, fullDate } from "../../utils/dates";
import { addDays } from "date-fns";
import { deleteFileFromS3, uploadFileToS3 } from "../../utils/aws";

const CompanyInfo = ({ params, org }) => {
  const { fetch, requestFetch } = useContext(FetchContext);
  const { snack } = useContext(SnackbarContext);
  const { auth } = useAuth();
  const { departmentFilter } = useDepartmentFilter();

  const [dialog, setDialog] = useState({ org: null });
  const [initForm, setInitForm] = useState({});
  const [avatarEditDialog, setAvatarEditDialog] = useState(null);

  const {
    loading: closedCorpPlansLoading,
    data: closedCorpPlansData,
    refetch: closedCorpPlansRefetch,
  } = useQuery(GET_CLOSED_CORP_PLANS, {
    variables: { organization: params.org },
  });

  const [updateProfilePicture] = useMutation(UPDATE_PROFILE_PIC);
  const [getSignedUrlS3] = useMutation(GET_SIGNED_URL_S3);

  const handleEditOrg = () => {
    const { name, fiscalYear, viewPreferences } = org;
    setInitForm({
      name,
      fiscalYear: parseInt(fiscalYear),
      viewPreferences: viewPreferences.map(({ name, position, show }) => ({ name, position, show })),
    });
    setDialog({ ...dialog, org: true });
  };

  const handleEditClose = () => {
    setDialog({ org: null, core: null, bhag: null });
  };

  const handleAvatarEditDialog = (open) => () => {
    setAvatarEditDialog(open);
  };

  const handleUploadImage = async (e) => {
    if (!_.isEmpty(e.target.files) && e.target.validity.valid) {
      const file = e.target.files[0];
      setAvatarEditDialog(file);
    }
  };

  const handleSaveImage = (ref) => async () => {
    const canvas = ref.current.getImage();
    canvas.toBlob(async (blob) => {
      const compressionOptions = { maxSizeMB: 1 };
      const compressedFile = await imageCompression(blob, compressionOptions);
      const fileType = compressedFile.type;
      const extension = _.last(fileType.split("/"));

      try {
        const profilePicUrl = await uploadFileToS3({
          snack,
          typeStr: "company picture",
          file: compressedFile,
          fileName: `organizations/${params.org}/public/${v4()}.${extension}`,
          fileType,
          getSignedUrlAsync: async (variables) =>
            getSignedUrlS3({
              variables,
            }),
        });

        if (!profilePicUrl) return;

        const ok = await updateProfilePicture({
          variables: {
            id: params.org,
            profilePicture: profilePicUrl,
          },
        });

        if (ok.data.updateOrganization) {
          snack("Updated company picture");

          if (profilePicture) await deleteImage("previous company picture");

          requestFetch();
          setAvatarEditDialog(null);
        }
      } catch (err) {
        snack("Failed to update company picture", "error");
      }
    });
  };

  const handleDeleteImage = async () => {
    try {
      const ok = await updateProfilePicture({ variables: { id: params.org, profilePicture: "" } });

      if (ok.data.updateOrganization) {
        snack("Deleted company picture");
        await deleteImage();

        requestFetch();
      }
    } catch (err) {
      snack("Failed to delete company picture", "error");
    }
  };

  const deleteImage = async (typeStr = "company picture") => {
    return await deleteFileFromS3({
      snack,
      typeStr,
      objectUrl: profilePicture,
      getSignedUrlAsync: async (variables) =>
        getSignedUrlS3({
          variables,
        }),
    });
  };

  const clearPreviousImageUpload = (e) => {
    // Need to clear the value of the input because the onChange doesn't fire upon attempting to select the same image file after an image upload cancellation
    e.target.value = null;
  };

  useEffect(() => {
    closedCorpPlansRefetch();
  }, [fetch, departmentFilter]);

  if (closedCorpPlansLoading) return <Loading />;

  const mostRecentClosedCorpPlan = _.orderBy(_.get(closedCorpPlansData, "corporatePlans", []), (plan) => parseInt(plan.year), ["desc"])[0];
  const minYear = mostRecentClosedCorpPlan ? addDays(parseInt(_.get(mostRecentClosedCorpPlan, "year")), 1) : undefined;

  const { name, profilePicture, fiscalYear } = org;

  return (
    <>
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={2}>
                <Typography variant="h5" className={styles.label}>
                  Company Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={10}>
                <div className={styles.avatarWrapper}>
                  <Avatar variant="square" className={styles.avatar} src={profilePicture}>
                    {name[0]}
                  </Avatar>
                  {isAuthed(auth, "company admin") && (
                    <Menu className={styles.editAvatar} button="Edit">
                      <input
                        name="file"
                        onChange={handleUploadImage}
                        onClick={clearPreviousImageUpload}
                        accept="image/*"
                        id="file-upload"
                        type="file"
                        className={styles.input}
                      />
                      <label htmlFor="file-upload">
                        <MenuItem component="span">Upload New</MenuItem>
                      </label>
                      {profilePicture && (
                        <MenuItem className={styles.delete} onClick={handleDeleteImage}>
                          Delete
                        </MenuItem>
                      )}
                    </Menu>
                  )}
                </div>
                <Typography variant="h6" className={styles.label}>
                  Company Name
                </Typography>
                <div className={styles.flexParagraph}>
                  <Typography variant="h5">{name}</Typography>
                  {isAuthed(auth, "company admin") && (
                    <Button onClick={handleEditOrg} variant="outlined" className={styles.editButton}>
                      Edit
                    </Button>
                  )}
                </div>

                <Typography variant="h6" className={styles.label}>
                  Current Fiscal Year
                </Typography>
                <div className={styles.flexParagraph}>
                  <Icon path={mdiCalendar} size={1.5} color="rgba(0, 0, 0, 0.65)" className={styles.icon} />
                  <Typography variant="h5">
                    {fullDate(fiscalYearStart(fiscalYear))} - {fullDate(fiscalYear)}
                  </Typography>
                </div>
                <Divider className={styles.divider} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>

      <AvatarEditDialog
        open={Boolean(avatarEditDialog)}
        handleClose={handleAvatarEditDialog(false)}
        handleSave={handleSaveImage}
        src={avatarEditDialog}
      />
      <EditDialog id={params.org} open={dialog} handleClose={handleEditClose} initForm={initForm} minYear={minYear} />
    </>
  );
};

export default CompanyInfo;

const UPDATE_PROFILE_PIC = gql`
  mutation TheCore_UpdateProfilePic($id: ID!, $profilePicture: String!) {
    updateOrganization(id: $id, profilePicture: $profilePicture)
  }
`;

const GET_CLOSED_CORP_PLANS = gql`
  query GetCorpPlans($organization: ID!) {
    corporatePlans: plans(organization: $organization, departmentName: "Corporate", category: "1 year", closed: true) {
      id
      year
      closed
    }
  }
`;

const GET_SIGNED_URL_S3 = gql`
  mutation ($fileName: String!, $fileType: String, $operation: String!) {
    generateSignedUrlS3(fileName: $fileName, fileType: $fileType, operation: $operation)
  }
`;
